import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/query-keys';

export type StatusCategory = 'backlog' | 'ready' | 'with_us' | 'with_client' | 'completed';

export interface PMStatus {
  name: string;
  description: string;
  sort_order: number;
  is_default_new?: boolean;
  is_terminal?: boolean;
  category?: StatusCategory;
  clickup_status_name?: string;
}

export interface PMPriority {
  name: string;
  description: string;
  sort_order: number;
  is_default?: boolean;
}

export interface DueDateRules {
  use_business_days: boolean;
  default_offset_days: number;
  urgent_offset_days: number;
  high_offset_days: number;
  low_offset_days: number;
  excluded_days: number[];
}

export interface AssigneeRules {
  prefer_agency_team: boolean;
  client_tasks_leave_unassigned: boolean;
  default_assignee: string | null;
  status_assignee_rules: Record<string, { clear_assignee?: boolean }>;
}

export interface WorkspacePMSettings {
  id: string;
  user_id: string;
  statuses: PMStatus[];
  priorities: PMPriority[];
  due_date_rules: DueDateRules | null;
  assignee_rules: AssigneeRules | null;
  pm_guidelines: string | null;
  external_integrations: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

// Default settings for new users
export const DEFAULT_STATUSES: PMStatus[] = [
  { name: 'Backlog', description: 'Not yet started, needs planning', is_default_new: true, sort_order: 1, category: 'backlog' },
  { name: 'Planned', description: 'Ready to start, in pipeline', sort_order: 2, category: 'ready' },
  { name: 'In Progress', description: 'Currently being worked on', sort_order: 3, category: 'with_us' },
  { name: 'Internal Review', description: 'Done, needs internal QA', sort_order: 4, category: 'with_us' },
  { name: 'Ready for Client Review', description: 'Send to client for approval', sort_order: 5, category: 'with_us' },
  { name: 'Client Reviewing', description: 'Waiting on client feedback', sort_order: 6, category: 'with_client' },
  { name: 'Revision Needed', description: 'Client requested changes', sort_order: 7, category: 'with_us' },
  { name: 'Waiting on Client', description: 'Blocked on client action', sort_order: 8, category: 'with_client' },
  { name: 'Blocked', description: 'Blocked on external dependency', sort_order: 9, category: 'with_us' },
  { name: 'On Hold (Deprioritized)', description: 'Not actively working', sort_order: 10, category: 'backlog' },
  { name: 'Ready for Delivery', description: 'Complete, ready to hand off', sort_order: 11, category: 'with_us' },
  { name: 'Complete', description: 'Done and delivered', is_terminal: true, sort_order: 12, category: 'completed' },
];

export const DEFAULT_PRIORITIES: PMPriority[] = [
  { name: 'Urgent', description: 'Drop everything, critical blocker', sort_order: 1 },
  { name: 'High', description: 'Important, address within 1-2 business days', sort_order: 2 },
  { name: 'Normal', description: 'Standard work, default priority', is_default: true, sort_order: 3 },
  { name: 'Low', description: 'Nice to have, when time permits', sort_order: 4 },
];

const DEFAULT_DUE_DATE_RULES: DueDateRules = {
  use_business_days: true,
  default_offset_days: 5,
  urgent_offset_days: 1,
  high_offset_days: 3,
  low_offset_days: 10,
  excluded_days: [0, 6],
};

const DEFAULT_ASSIGNEE_RULES: AssigneeRules = {
  prefer_agency_team: true,
  client_tasks_leave_unassigned: false,
  default_assignee: null,
  status_assignee_rules: {},
};

const DEFAULT_PM_GUIDELINES = `We are Media Garcia, a small and agile IT services company that specializes in helping small and mid-sized businesses with CRM software, mainly HubSpot. Our work includes migrating systems, configuration, troubleshooting, reporting/data/visualization (including data cleanup), automation, marketing, and other related services.

Task Naming Conventions:
- Use clear, professional, and intuitive task names
- Prefix AI-suggested tasks with "AI Suggestion: "
- Spell out acronyms (e.g., eCOA = electronic Clinical Outcome Assessment)

Description Standards:
- Include video timestamps (e.g., "14:45-15:30") for context
- Summarize context, outcome, and purpose
- Goal: developers can implement without uncertainty about what is being asked

Status Flow:
- New tasks start in Backlog
- Tasks requiring client action go to "Waiting on Client"
- Complete = delivered AND confirmed by client

Priority Guidelines:
- Urgent = production issues, client escalations, critical blockers
- High = committed deliverables this week
- Normal = planned work (default)
- Low = backlog ideas, nice-to-haves

Due Date Guidelines:
- Only add due dates that are realistic given workload
- Use business-day offsets from today
- If explicitly discussed in conversation, use the discussed date

Assignee Guidelines:
- Assign to internal team members based on conversation context
- Keep tasks assigned even when waiting on client action`;

export function useWorkspacePMSettings() {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: queryKeys.workspace.pmSettings,
    queryFn: async () => {
      if (!user) return null;

      // Try to get existing settings
      const { data, error } = await supabase
        .from('workspace_pm_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        throw error;
      }

      if (data) {
        return {
          ...data,
          statuses: data.statuses as unknown as PMStatus[],
          priorities: data.priorities as unknown as PMPriority[],
          due_date_rules: data.due_date_rules as unknown as DueDateRules | null,
          assignee_rules: data.assignee_rules as unknown as AssigneeRules | null,
        } as WorkspacePMSettings;
      }

      // Create default settings if none exist
      const { data: newSettings, error: insertError } = await supabase
        .from('workspace_pm_settings')
        .insert({
          user_id: user.id,
          statuses: DEFAULT_STATUSES as unknown as Record<string, unknown>[],
          priorities: DEFAULT_PRIORITIES as unknown as Record<string, unknown>[],
          due_date_rules: DEFAULT_DUE_DATE_RULES as unknown as Record<string, unknown>,
          assignee_rules: DEFAULT_ASSIGNEE_RULES as unknown as Record<string, unknown>,
          pm_guidelines: DEFAULT_PM_GUIDELINES,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      return {
        ...newSettings,
        statuses: newSettings.statuses as unknown as PMStatus[],
        priorities: newSettings.priorities as unknown as PMPriority[],
        due_date_rules: newSettings.due_date_rules as unknown as DueDateRules | null,
        assignee_rules: newSettings.assignee_rules as unknown as AssigneeRules | null,
      } as WorkspacePMSettings;
    },
    enabled: !!user,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<{
      statuses: PMStatus[];
      priorities: PMPriority[];
      due_date_rules: DueDateRules;
      assignee_rules: AssigneeRules;
      pm_guidelines: string;
    }>) => {
      if (!user || !settingsQuery.data) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('workspace_pm_settings')
        .update(updates as unknown as Record<string, unknown>)
        .eq('id', settingsQuery.data.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workspace.pmSettings });
      toast({ title: 'Settings saved' });
    },
    onError: (error) => {
      toast({
        title: 'Error saving settings',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    settings: settingsQuery.data,
    isLoading: settingsQuery.isLoading,
    error: settingsQuery.error,
    updateSettings,
  };
}
